import React, { useEffect, useRef, useState } from "react";
import { CHAINS } from "../config/chains";
import { getTempoFeeTokenSymbols } from "../utils/tempoFeeTokens";
import { fundingSelectionKey, useWalletFunding } from "../hooks/useWalletFunding";

/* ============================================================================
   Onboarding — first-run setup for a wallet with no Stabletrust Pay account on
   the selected chain. Two steps:
     1. Add funds  — the stablecoin to deposit + gas to pay network fees.
     2. Create account — sign for the encryption key, approve the create tx.

   Presentation plus the wallet funding check. Account creation itself stays in
   TabbedOperations (the same sign → ensureAccount path the app already uses),
   which reports progress back through setupStage / setupError.

   Inline-styled like MobileShell / BalancesModal. Desktop: a centered card over
   the page, under the app header. Mobile: a full-screen sheet with a sticky CTA.
   ========================================================================== */

const O = {
  serif: "'Instrument Serif', Georgia, serif",
  mono: "'JetBrains Mono', ui-monospace, Menlo, monospace",
  sans: "'Space Grotesk', -apple-system, sans-serif",
  ink: "#16181A",
  inkMid: "#4A4A46",
  inkSoft: "#8E8E88",
  inkFaint: "#A5A5A0",
  body: "#6E6E68",
  rule: "#E6E6E1",
  ruleHair: "#F0F0EC",
  ruleInner: "#F5F5F1",
  accent: "#56AEF0",
  accentInk: "#08222F",
  link: "#3E8FCC",
  disabledBg: "#F1F1ED",
  disabledFg: "#AFAFA9",
};

// Same tint families as the header gas chip and BalancesModal.
const TONE = {
  ok: { ink: "#3E8C68", line: "#C4E0D0", fill: "#F8FCFA" },
  warn: { ink: "#B08A2E", line: "#E4D2A8", fill: "#FDFBF5" },
  danger: { ink: "#C4462E", line: "#E4B9AE", fill: "#FDF8F7" },
};

const SAFE_TOP = "env(safe-area-inset-top, 0px)";
const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";

const TITLE_ID = "onboarding-title";
const IN_FLIGHT_STAGES = ["signing", "confirming", "activating"];

const formatAmount = (value, digits) => {
  if (value == null) return "—";
  // Dust that would round to all zeros (e.g. 0.0000 ETH) — show a floor
  // instead of looking empty next to a green check.
  if (value > 0 && Number(value.toFixed(digits)) === 0) {
    return `<${(10 ** -digits).toFixed(digits)}`;
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const OnboardingStyles = () => (
  <style>{`
    @keyframes ob-spin { to { transform: rotate(360deg); } }
    @keyframes ob-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes ob-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
    .ob-press:active { transform: scale(0.985); }
    .ob-link:hover { color: #1F6FAB !important; }
    .ob-quiet:hover { background: #FAFAF8 !important; }
    .ob-pill:hover { background: #FAFAF8 !important; }
    .ob-root *:focus-visible { outline: 2px solid ${O.ink}; outline-offset: 2px; }
    @media (prefers-reduced-motion: reduce) {
      .ob-root, .ob-root * { animation: none !important; transition: none !important; }
    }
  `}</style>
);

/* ---------------------------------------------------------------- pieces */

function Spinner({ size = 14, color = O.link, track = "#D8E9F7" }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: "none",
        boxSizing: "border-box",
        borderRadius: "50%",
        border: `1.6px solid ${track}`,
        borderTopColor: color,
        display: "inline-block",
        animation: "ob-spin .8s linear infinite",
      }}
    />
  );
}

function CheckIcon({ size = 12, color = "#FFFFFF", strokeWidth = 2.4 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5l4.2 4.2L19 7" />
    </svg>
  );
}

function OkDot({ size = 20 }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: "50%",
        background: TONE.ok.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CheckIcon size={Math.round(size * 0.55)} />
    </span>
  );
}

/** Asset icon with an optional small chain badge, as on the network pills. */
function AssetIcon({ iconUrl, badgeUrl, size = 30 }) {
  const badge = Math.round(size * 0.46);
  return (
    <span style={{ position: "relative", width: size, height: size, flex: "none" }}>
      <span
        style={{
          display: "block",
          width: size,
          height: size,
          borderRadius: "50%",
          background: iconUrl ? `url(${iconUrl}) center/contain no-repeat` : O.ruleInner,
        }}
      />
      {badgeUrl && badgeUrl !== iconUrl && (
        <span
          style={{
            position: "absolute",
            right: -3,
            bottom: -3,
            width: badge,
            height: badge,
            boxSizing: "border-box",
            borderRadius: "50%",
            border: "1.5px solid #FFFFFF",
            background: `#FFFFFF url(${badgeUrl}) center/contain no-repeat`,
          }}
        />
      )}
    </span>
  );
}

const captionStyle = {
  fontFamily: O.mono,
  fontSize: 10,
  letterSpacing: "0.05em",
  color: O.inkSoft,
};

const bodyTextStyle = {
  margin: 0,
  fontFamily: O.sans,
  fontSize: 14,
  lineHeight: 1.6,
  color: O.body,
};

function Notice({ tone, children }) {
  const t = TONE[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      style={{
        marginBottom: 12,
        padding: "11px 13px",
        border: `1px solid ${t.line}`,
        background: t.fill,
        color: t.ink,
        fontFamily: O.mono,
        fontSize: 11,
        lineHeight: 1.65,
      }}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ label, onClick, disabled, busy, isMobile }) {
  const inactive = disabled || busy;
  return (
    <button
      type="button"
      className={inactive ? undefined : "ob-press"}
      onClick={inactive ? undefined : onClick}
      disabled={inactive}
      aria-busy={busy || undefined}
      style={{
        width: "100%",
        minHeight: isMobile ? 54 : 56,
        border: 0,
        borderRadius: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        background: inactive ? O.disabledBg : O.accent,
        color: busy ? O.inkMid : inactive ? O.disabledFg : O.accentInk,
        fontFamily: O.sans,
        fontSize: 15.5,
        fontWeight: 600,
        cursor: inactive ? "default" : "pointer",
        transition: "background .15s, transform .08s",
      }}
    >
      {busy && <Spinner size={15} />}
      <span>{label}</span>
    </button>
  );
}

/** Secondary CTA beside the primary — bordered pill, same height. */
function SecondaryButton({ label, onClick, isMobile }) {
  return (
    <button
      type="button"
      className="ob-quiet ob-press"
      onClick={onClick}
      style={{
        flex: "none",
        alignSelf: "stretch",
        minHeight: isMobile ? 54 : 56,
        padding: "0 18px",
        border: `1px solid ${O.rule}`,
        borderRadius: 999,
        background: "#FFFFFF",
        fontFamily: O.sans,
        fontSize: 15.5,
        fontWeight: 600,
        color: O.ink,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background .15s, transform .08s",
      }}
    >
      {label}
    </button>
  );
}

function TextButton({ children, onClick }) {
  return (
    <button
      type="button"
      className="ob-link"
      onClick={onClick}
      style={{
        display: "block",
        margin: "10px auto 0",
        minHeight: 40,
        padding: "0 8px",
        border: 0,
        background: "transparent",
        fontFamily: O.mono,
        fontSize: 11,
        letterSpacing: "0.04em",
        color: O.link,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function FootHint({ children }) {
  return (
    <div
      style={{
        paddingTop: 10,
        textAlign: "center",
        fontFamily: O.mono,
        fontSize: 10.5,
        lineHeight: 1.6,
        color: O.inkSoft,
      }}
    >
      {children}
    </div>
  );
}

function NetworkPicker({ chain, token, onClick, disabled }) {
  const label = chain
    ? token
      ? `${chain.name} · ${token.symbol}`
      : chain.name
    : "Select a network";
  return (
    <button
      type="button"
      className={disabled ? undefined : "ob-pill"}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={`Network: ${label}${disabled ? "" : ". Change network"}`}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 11,
        minHeight: 50,
        padding: "10px 14px",
        border: `1px solid ${O.rule}`,
        background: "#FFFFFF",
        textAlign: "left",
        cursor: disabled ? "default" : "pointer",
        transition: "background .15s",
      }}
    >
      <AssetIcon iconUrl={token?.icon || chain?.icon} badgeUrl={token ? chain?.icon : null} size={24} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: O.mono,
          fontSize: 11.5,
          letterSpacing: "0.04em",
          color: O.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {!disabled && (
        <span style={{ flex: "none", fontFamily: O.mono, fontSize: 10.5, letterSpacing: "0.04em", color: O.link }}>
          Change
        </span>
      )}
    </button>
  );
}

function StepMarker({ number, state }) {
  const done = state === "done";
  const current = state === "current";
  return (
    <span
      style={{
        width: 28,
        height: 28,
        flex: "none",
        boxSizing: "border-box",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: O.mono,
        fontSize: 11.5,
        background: done ? TONE.ok.ink : current ? O.ink : "#FFFFFF",
        color: done || current ? "#FFFFFF" : O.inkFaint,
        border: done || current ? "none" : `1px solid ${O.rule}`,
        transition: "background .2s",
      }}
    >
      {done ? <CheckIcon size={13} /> : number}
    </span>
  );
}

/** One numbered step. Only the current step shows its body; others show a one-line summary. */
function StepSection({ number, state, title, summary, isMobile, children }) {
  const current = state === "current";
  return (
    <li
      aria-current={current ? "step" : undefined}
      style={{ borderTop: `1px solid ${O.ruleHair}`, padding: isMobile ? "18px 0" : "20px 0" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <StepMarker number={number} state={state} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: O.sans,
              fontSize: 16,
              fontWeight: 500,
              color: state === "upcoming" ? O.inkSoft : O.ink,
            }}
          >
            {title}
          </div>
          {!current && summary && (
            <div style={{ ...captionStyle, letterSpacing: "0.02em", paddingTop: 3 }}>{summary}</div>
          )}
        </div>
        {state === "done" && (
          <span style={{ flex: "none", fontFamily: O.mono, fontSize: 10, letterSpacing: "0.05em", color: TONE.ok.ink }}>
            Done
          </span>
        )}
      </div>
      {current && (
        <div style={{ paddingLeft: isMobile ? 0 : 42, paddingTop: 14, animation: "ob-fade .2s ease both" }}>
          {children}
        </div>
      )}
    </li>
  );
}

function FundRow({ iconUrl, badgeUrl, symbol, caption, amount, digits, ok, loading, first }) {
  let status;
  if (ok === true) {
    status = <OkDot />;
  } else if (ok === false) {
    status = null;
  } else if (loading) {
    status = <Spinner size={14} />;
  } else {
    status = <span style={{ ...captionStyle, color: O.inkFaint }}>—</span>;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderTop: first ? "none" : `1px solid ${O.ruleInner}`,
      }}
    >
      <AssetIcon iconUrl={iconUrl} badgeUrl={badgeUrl} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: O.sans, fontSize: 15, fontWeight: 500, color: O.ink }}>{symbol}</div>
        <div style={{ ...captionStyle, letterSpacing: "0.02em", paddingTop: 2 }}>{caption}</div>
      </div>
      <div style={{ flex: "none", fontFamily: O.mono, fontSize: 12, color: O.inkMid, textAlign: "right" }}>
        {amount == null ? "" : formatAmount(amount, digits)}
      </div>
      {status != null && (
        <div style={{ flex: "none", minWidth: 58, display: "flex", justifyContent: "flex-end" }}>{status}</div>
      )}
    </div>
  );
}

/** A sub-step inside "Create account": pending → active → done (or error). */
function ProgressItem({ state, title, detail, last }) {
  const circle = {
    width: 22,
    height: 22,
    flex: "none",
    boxSizing: "border-box",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  let marker;
  if (state === "done") {
    marker = (
      <span style={{ ...circle, background: TONE.ok.ink }}>
        <CheckIcon size={11} />
      </span>
    );
  } else if (state === "active") {
    marker = (
      <span style={{ ...circle, background: "#EAF6FE" }}>
        <Spinner size={12} />
      </span>
    );
  } else if (state === "error") {
    marker = (
      <span
        style={{
          ...circle,
          border: `1px solid ${TONE.danger.line}`,
          background: TONE.danger.fill,
          color: TONE.danger.ink,
          fontFamily: O.mono,
          fontSize: 11,
        }}
      >
        !
      </span>
    );
  } else {
    marker = <span style={{ ...circle, border: `1px solid ${O.rule}` }} />;
  }

  return (
    <li style={{ display: "flex", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {marker}
        {!last && <span style={{ flex: 1, width: 1, minHeight: 12, background: O.rule, margin: "4px 0" }} />}
      </div>
      <div style={{ minWidth: 0, paddingTop: 2, paddingBottom: last ? 0 : 16 }}>
        <div
          style={{
            fontFamily: O.sans,
            fontSize: 14,
            fontWeight: 500,
            color: state === "pending" ? O.inkMid : O.ink,
          }}
        >
          {title}
        </div>
        {detail ? (
          <div style={{ fontFamily: O.mono, fontSize: 10.5, lineHeight: 1.65, color: O.inkSoft, paddingTop: 2 }}>
            {detail}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/* ================================================================ onboarding */

/**
 * @param {object} props
 * @param {boolean} props.isMobile
 * @param {string} props.address - connected wallet
 * @param {string|null} props.chainKey - CHAINS key of the selected network
 * @param {object|null} props.token - selected token config (symbol, icon, address, decimals); null when none selectable
 * @param {() => void} props.onChangeNetwork - opens the existing network/token picker
 * @param {() => void} props.onSkip
 * @param {boolean} props.accountPending - create tx already landed, waiting for finalization
 * @param {'idle'|'signing'|'confirming'|'activating'|'done'} props.setupStage
 * @param {{kind: 'signature'|'transaction'|'funds', message: string}|null} props.setupError
 * @param {boolean} props.keyReady - encryption key already derived (cached), so no signature needed
 * @param {() => void} props.onCreateAccount
 * @param {() => void} props.onComplete - account is active; land on Deposit
 */
export default function Onboarding({
  isMobile,
  address,
  chainKey,
  token,
  onChangeNetwork,
  onSkip,
  accountPending,
  setupStage = "idle",
  setupError,
  keyReady,
  onCreateAccount,
  onComplete,
}) {
  const chain = chainKey ? CHAINS[chainKey] : null;
  const chainName = chain?.name || "this network";
  const isTestnet = chain?.network !== "mainnet";
  const isTempo = chainKey === "tempo";
  const tokenSymbol = token?.symbol || "stablecoins";
  const nativeSymbol = chain?.nativeCurrency?.symbol || "ETH";
  const gasNoun = isTempo ? "a fee token" : nativeSymbol;
  // Chains like Arc pay gas in the same stablecoin being deposited — one row.
  const sharedAsset = !!token && !isTempo && nativeSymbol === token.symbol;

  const [confirmedKey, setConfirmedKey] = useState(null);
  const [fundingNote, setFundingNote] = useState(null); // { tone, text, allowContinue }
  const [userChecking, setUserChecking] = useState(false);
  const [copied, setCopied] = useState(false);

  const isDone = setupStage === "done";
  const inFlight = IN_FLIGHT_STAGES.includes(setupStage);
  // Once account creation has started (or already landed), funding is moot.
  const setupStarted = setupStage !== "idle" || !!accountPending;

  const selectionKey = fundingSelectionKey(address, chainKey, token?.address);
  const fundingConfirmed = setupStarted || confirmedKey === selectionKey;
  const funding = useWalletFunding({
    account: address,
    chainKey,
    token,
    enabled: !!token && !fundingConfirmed,
  });
  const currentStep = fundingConfirmed ? 2 : 1;

  const latestKeyRef = useRef(selectionKey);
  latestKeyRef.current = selectionKey;

  // Already funded when onboarding opens (or after switching network)? Mark
  // step 1 done straight away. Only the first read per selection does this —
  // afterwards moving on is the user's click.
  const autoCheckedKeyRef = useRef(null);
  useEffect(() => {
    if (!funding.checked || autoCheckedKeyRef.current === selectionKey) return;
    autoCheckedKeyRef.current = selectionKey;
    if (funding.gas?.ok && funding.token?.ok) setConfirmedKey(selectionKey);
  }, [funding.checked, funding.gas, funding.token, selectionKey]);

  useEffect(() => {
    setFundingNote(null);
  }, [selectionKey]);

  const handleCheckFunds = async () => {
    setFundingNote(null);
    setUserChecking(true);
    let result;
    try {
      result = await funding.refresh();
    } finally {
      setUserChecking(false);
    }
    if (!result || result.key !== latestKeyRef.current) return;

    const { gas, token: tokenBalance } = result;
    if (gas.ok == null || tokenBalance.ok == null) {
      setFundingNote({
        tone: "danger",
        text: `We couldn't reach ${chainName} to check your balances. Try again in a moment.`,
      });
    } else if (gas.ok && tokenBalance.ok) {
      setConfirmedKey(result.key);
    } else if (sharedAsset || (!gas.ok && !tokenBalance.ok)) {
      setFundingNote({
        tone: "warn",
        text: `We don't see ${sharedAsset ? tokenSymbol : `${tokenSymbol} or ${gasNoun}`} on ${chainName} yet. Transfers can take a minute to arrive.`,
      });
    } else if (!gas.ok) {
      setFundingNote({
        tone: "warn",
        text: `We don't see ${gasNoun} on ${chainName} yet. You need a little to pay network fees.`,
      });
    } else {
      setFundingNote({
        tone: "warn",
        text: `Your ${isTempo ? "fee token" : nativeSymbol} arrived, but there's no ${tokenSymbol} yet. You can create your account now and add ${tokenSymbol} before you deposit.`,
        allowContinue: true,
      });
    }
  };

  const copyTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(copyTimerRef.current), []);
  const handleCopy = () => {
    if (!address) return;
    navigator.clipboard?.writeText(address).catch(() => {});
    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 1600);
  };

  // "You're all set" stays until the user leaves it (button or Close); the
  // guard stops a double click from finishing twice.
  const completedRef = useRef(false);
  const complete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  };

  // Page behind stays put; focus moves into the dialog for keyboard / SR users.
  const dialogRef = useRef(null);
  useEffect(() => {
    dialogRef.current?.focus({ preventScroll: true });
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = "hidden";
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, []);

  /* ------------------------------------------------------------ step 1 */

  const gasIcon = nativeSymbol === "ETH" ? "/ethereum.png" : chain?.icon;
  const fundRows = !token
    ? []
    : sharedAsset
      ? [
          {
            key: "asset",
            iconUrl: token.icon,
            badgeUrl: chain?.icon,
            symbol: tokenSymbol,
            caption: "Deposits and network fees",
            amount: funding.token?.amount ?? funding.gas?.amount,
            digits: 2,
            ok:
              funding.token?.ok == null || funding.gas?.ok == null
                ? null
                : funding.token.ok && funding.gas.ok,
          },
        ]
      : [
          {
            key: "token",
            iconUrl: token.icon,
            badgeUrl: chain?.icon,
            symbol: tokenSymbol,
            caption: "To deposit",
            amount: funding.token?.amount,
            digits: 2,
            ok: funding.token?.ok,
          },
          {
            key: "gas",
            iconUrl: isTempo ? chain?.icon : gasIcon,
            badgeUrl: chain?.icon,
            symbol: isTempo ? funding.gas?.symbol || "Fee token" : nativeSymbol,
            caption: isTempo
              ? `Network fees · ${getTempoFeeTokenSymbols().join(", ")}`
              : "Network fees",
            amount: funding.gas?.amount,
            digits: isTempo ? 2 : 4,
            ok: funding.gas?.ok,
          },
        ];

  const step1Title = !token
    ? "Add funds"
    : sharedAsset
      ? `Add ${tokenSymbol}`
      : `Add ${tokenSymbol} and ${isTempo ? "a fee token" : nativeSymbol}`;

  const step1Summary =
    funding.checked && token
      ? sharedAsset
        ? `${formatAmount(funding.token?.amount ?? funding.gas?.amount, 2)} ${tokenSymbol} in your wallet`
        : `${formatAmount(funding.token?.amount, 2)} ${tokenSymbol} · ${formatAmount(
            funding.gas?.amount,
            isTempo ? 2 : 4,
          )} ${isTempo ? funding.gas?.symbol || "fee token" : nativeSymbol} in your wallet`
      : "Wallet funded";

  const step1Body = (
    <>
      <p style={bodyTextStyle}>
        {!token ? (
          <>Choose a network with a supported stablecoin to see what to add.</>
        ) : sharedAsset ? (
          <>
            On {chainName}, {tokenSymbol} covers both your deposit and network fees. Send some to your wallet to
            continue.
          </>
        ) : (
          <>
            Deposit {tokenSymbol} and {isTempo ? "a fee token" : nativeSymbol} on {chainName} network.
          </>
        )}
      </p>

      {fundRows.length > 0 && (
        <div style={{ marginTop: 14, border: `1px solid ${O.rule}` }}>
          {fundRows.map((row, i) => (
            <FundRow
              key={row.key}
              {...row}
              first={i === 0}
              loading={!funding.checked || funding.checking}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <div style={captionStyle}>Your wallet address</div>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? "Address copied" : "Copy wallet address"}
          style={{
            width: "100%",
            marginTop: 7,
            padding: "11px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            border: `1px solid ${O.rule}`,
            background: "#FFFFFF",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <span style={{ fontFamily: O.mono, fontSize: 11.5, lineHeight: 1.6, color: O.ink, wordBreak: "break-all" }}>
            {address}
          </span>
          <span
            style={{
              flex: "none",
              fontFamily: O.mono,
              fontSize: 10.5,
              letterSpacing: "0.04em",
              color: copied ? TONE.ok.ink : O.link,
            }}
          >
            {copied ? "Copied" : "Copy"}
          </span>
        </button>
        <div style={{ fontFamily: O.mono, fontSize: 10.5, lineHeight: 1.7, color: O.inkSoft, paddingTop: 8 }}>
          Send on {chainName} only. Funds sent on another network can't be recovered.
        </div>
      </div>

      {isTestnet && (
        <div style={{ fontFamily: O.mono, fontSize: 11, lineHeight: 1.7, color: O.inkMid, paddingTop: 12 }}>
          Need test funds?{" "}
          <a
            className="ob-link"
            href="/faucets.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: O.link, textDecoration: "none" }}
          >
            Get testnet tokens ↗
          </a>
        </div>
      )}
    </>
  );

  /* ------------------------------------------------------------ step 2 */

  const errorKind = setupError?.kind;
  const signState =
    keyReady || ["confirming", "activating", "done"].includes(setupStage)
      ? "done"
      : setupStage === "signing"
        ? "active"
        : errorKind === "signature"
          ? "error"
          : "pending";
  const txState = ["activating", "done"].includes(setupStage)
    ? "done"
    : setupStage === "confirming"
      ? "active"
      : errorKind === "transaction" || errorKind === "funds"
        ? "error"
        : "pending";
  const activationState = isDone ? "done" : setupStage === "activating" ? "active" : "pending";

  const step2Body = (
    <>
      <p style={bodyTextStyle}>
        Your account keeps your balance encrypted onchain.{" "}
        {keyReady
          ? "Approve one transaction in your wallet to create it."
          : "Creating it takes two quick approvals in your wallet."}
      </p>
      <ol style={{ listStyle: "none", margin: "16px 0 0", padding: 0 }}>
        <ProgressItem
          state={signState}
          title={keyReady ? "Encryption key ready" : "Sign to create your encryption key"}
        />
        <ProgressItem state={txState} title="Approve the account transaction" />
        <ProgressItem state={activationState} title="Activate your account" last />
      </ol>
    </>
  );

  /* ------------------------------------------------------------ layout */

  const header = (
    <div
      style={{
        flex: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        height: isMobile ? 52 : 56,
        padding: isMobile ? "0 20px" : "0 28px",
        borderBottom: `1px solid ${O.ruleHair}`,
      }}
    >
      {isMobile ? (
        <img
          src="/stabletrustpaylogo.png"
          alt="Stabletrust Pay"
          style={{ height: 18, width: "auto", display: "block" }}
        />
      ) : (
        <span style={{ ...captionStyle, fontSize: 10.5, letterSpacing: "0.08em" }}>Account setup</span>
      )}
      {isDone && (
        <button
          type="button"
          className="ob-quiet"
          onClick={complete}
          style={{
            minHeight: 44,
            padding: "0 2px",
            border: 0,
            background: "transparent",
            fontFamily: O.mono,
            fontSize: 10.5,
            letterSpacing: "0.06em",
            color: O.inkSoft,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      )}
    </div>
  );

  const content = isDone ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: isMobile ? "48px 4px 12px" : "20px 12px 4px",
        animation: "ob-rise .3s ease both",
      }}
    >
      <span
        style={{
          width: 64,
          height: 64,
          boxSizing: "border-box",
          borderRadius: "50%",
          border: `1px solid ${TONE.ok.line}`,
          background: TONE.ok.fill,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckIcon size={26} color={TONE.ok.ink} strokeWidth={2} />
      </span>
      <h2
        id={TITLE_ID}
        style={{ margin: "20px 0 0", fontFamily: O.serif, fontWeight: 400, fontSize: 34, lineHeight: 1.1, color: O.ink }}
      >
        You're all set
      </h2>
      <p style={{ ...bodyTextStyle, marginTop: 10, maxWidth: 360 }}>
        Your {chainName} account is active. Next, deposit {tokenSymbol} to start sending it privately.
      </p>
    </div>
  ) : (
    <>
      <h2
        id={TITLE_ID}
        style={{
          margin: 0,
          fontFamily: O.serif,
          fontWeight: 400,
          fontSize: isMobile ? 32 : 36,
          lineHeight: 1.08,
          color: O.ink,
        }}
      >
        Welcome to Stabletrust Pay
      </h2>
      <p style={{ ...bodyTextStyle, fontSize: 14.5, marginTop: 10 }}>
        Two quick steps and you're ready to send stablecoins privately.
      </p>

      <div style={{ marginTop: 20 }}>
        <div style={captionStyle}>Network</div>
        <div style={{ marginTop: 7 }}>
          <NetworkPicker chain={chain} token={token} onClick={onChangeNetwork} disabled={inFlight} />
        </div>
      </div>

      <ol style={{ listStyle: "none", margin: "22px 0 0", padding: 0 }}>
        <StepSection
          number={1}
          state={currentStep === 1 ? "current" : "done"}
          title={step1Title}
          summary={step1Summary}
          isMobile={isMobile}
        >
          {step1Body}
        </StepSection>
        <StepSection
          number={2}
          state={currentStep === 2 ? "current" : "upcoming"}
          title="Create your account"
          summary={currentStep === 1 ? "Sign and approve in your wallet" : null}
          isMobile={isMobile}
        >
          {step2Body}
        </StepSection>
      </ol>
    </>
  );

  const stageLabel = {
    signing: "Sign in your wallet…",
    confirming: "Confirm in your wallet…",
    activating: "Activating your account…",
  }[setupStage];

  let footer;
  if (isDone) {
    footer = (
      <>
        <PrimaryButton label="Go to deposit" onClick={complete} isMobile={isMobile} />
      </>
    );
  } else if (currentStep === 1) {
    footer = (
      <>
        {fundingNote && <Notice tone={fundingNote.tone}>{fundingNote.text}</Notice>}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <PrimaryButton
              label={!token ? "Choose a network to continue" : userChecking ? "Checking balances…" : "Continue"}
              busy={userChecking}
              disabled={!token}
              onClick={handleCheckFunds}
              isMobile={isMobile}
            />
          </div>
          <SecondaryButton label="Skip for now" onClick={onSkip} isMobile={isMobile} />
        </div>
        {fundingNote?.allowContinue && (
          <TextButton onClick={() => setConfirmedKey(selectionKey)}>Continue without {tokenSymbol}</TextButton>
        )}
      </>
    );
  } else {
    footer = (
      <>
        {setupError && <Notice tone="danger">{setupError.message}</Notice>}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <PrimaryButton
              label={stageLabel || (setupError ? "Try again" : "Create account")}
              busy={inFlight}
              onClick={onCreateAccount}
              isMobile={isMobile}
            />
          </div>
          {!inFlight && <SecondaryButton label="Skip for now" onClick={onSkip} isMobile={isMobile} />}
        </div>
        {(setupStage === "signing" || setupStage === "confirming") && (
          <FootHint>Don't see a request? Open your wallet to find it.</FootHint>
        )}
        {errorKind === "funds" && (
          <TextButton onClick={() => setConfirmedKey(null)}>Back to add funds</TextButton>
        )}
      </>
    );
  }

  if (isMobile) {
    return (
      <div
        ref={dialogRef}
        className="ob-root"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        style={{
          position: "fixed",
          inset: 0,
          // Above MobileShell, below its Sheets (z 40) so "Change" network
          // opens the picker on top of this screen.
          zIndex: 35,
          display: "flex",
          flexDirection: "column",
          background: "#FFFFFF",
          color: O.ink,
          paddingTop: SAFE_TOP,
          outline: "none",
          animation: "ob-fade .18s ease both",
        }}
      >
        <OnboardingStyles />
        {header}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "24px 20px 20px",
          }}
        >
          {content}
        </div>
        <div
          style={{
            flex: "none",
            borderTop: `1px solid ${O.ruleHair}`,
            background: "#FFFFFF",
            padding: `12px 20px calc(12px + ${SAFE_BOTTOM})`,
          }}
        >
          {footer}
        </div>
      </div>
    );
  }

  return (
    <div
      className="ob-root"
      style={{
        position: "fixed",
        inset: 0,
        // Under the site header (10002) so the wallet menu stays reachable,
        // and under the token selector (10010) that "Change" opens.
        zIndex: 10000,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "88px 16px 24px",
        background: "rgba(255,255,255,0.86)",
        animation: "ob-fade .18s ease both",
      }}
    >
      <OnboardingStyles />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        style={{
          width: "100%",
          maxWidth: 540,
          // The body scrolls inside the card, so the CTA stays in view on
          // short laptop screens instead of sitting below the fold.
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#FFFFFF",
          color: O.ink,
          border: `1px solid ${O.rule}`,
          boxShadow: "0 30px 70px -40px rgba(20,22,24,0.28)",
          outline: "none",
          animation: "ob-rise .26s cubic-bezier(.2,.8,.2,1) both",
        }}
      >
        {header}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "26px 28px 24px" }}>
          {content}
        </div>
        <div style={{ flex: "none", borderTop: `1px solid ${O.ruleHair}`, padding: "18px 28px 24px" }}>
          {footer}
        </div>
      </div>
    </div>
  );
}
